import { ReservationDetailDto } from '@/services/platform/api/model';
import { useClientTranslation } from '@/lib/core';
import { addToCalendar } from '@/lib/core/utils/calendar';
import { useBookingShare } from '@/lib/core/utils/share';
import { Icon } from '@/components/ui/icon';

// ── BookingDetailTools ────────────────────────────────────────────────────────

interface BookingDetailToolsProps {
  reservation: ReservationDetailDto;
  status: string;
  onCancelBooking: () => void;
}

export function BookingDetailTools({ reservation, status, onCancelBooking }: BookingDetailToolsProps) {
  const t = useClientTranslation();
  const { handleShare } = useBookingShare(reservation);

  async function handleAddToCalendar() {
    const title = reservation.venue?.name ?? reservation.name ?? t('App.Labels.Booking');
    const start = reservation.startDate;
    if (!start) return;
    await addToCalendar({ title, startDate: new Date(start) });
  }

  function handleDirections() {
    const lat = reservation.venue?.gpsLat;
    const lon = reservation.venue?.gpsLon;
    if (!lat || !lon) return;
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}&travelmode=driving`,
      '_blank'
    );
  }

  let allowCancellation = status?.toLowerCase() !== 'cancelled' && status?.toLowerCase() !== 'checkedout';

  if (reservation?.event) {
    if (!reservation?.event?.isPaid) {
      allowCancellation = status?.toLowerCase() !== 'cancelled';
    } else {
      allowCancellation = false;
    }
  }

  const buttons = [
    {
      id: 'btn-add-to-calendar',
      icon: 'CalendarPlus',
      label: t('App.Labels.AddToCalendar'),
      onClick: handleAddToCalendar,
      enabled: status?.toLowerCase() !== 'checkedout',
    },
    {
      id: 'btn-share',
      icon: 'ShareIos',
      label: t('App.Labels.ShareGuests'),
      onClick: handleShare,
    },
    {
      id: 'btn-directions',
      icon: 'MapPin',
      label: t('App.Labels.SeeDirections'),
      onClick: handleDirections,
      enabled: reservation?.serviceType !== 'Driver',
    },
    {
      id: 'btn-cancel-booking',
      icon: 'Xmark',
      label: t('App.Labels.CancelBooking'),
      onClick: onCancelBooking,
      enabled: allowCancellation,
    },
  ];

  return (
    <div className="flex flex-wrap">
      {buttons.map(({ id, icon, label, onClick, enabled = true }) => (
        <button
          key={icon}
          id={id}
          onClick={onClick}
          disabled={!enabled}
          className="flex min-w-0 flex-1 flex-col items-center disabled:opacity-40"
        >
          <div className="bg-jncquoi-neutral-50 flex size-11.5 items-center justify-center rounded-full p-2.5">
            <Icon
              name={icon}
              width={24}
              height={24}
              strokeWidth={1.5}
              className="text-jncquoi-neutral-1000"
            />
          </div>
          <div className="flex h-8 items-center justify-center px-1 py-4">
            <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-center text-[10px] font-medium tracking-[0.5px] whitespace-pre uppercase">
              {label}
            </span>
          </div>
        </button>
      ))}
    </div>
  );
}
